//THIS IS THE SIDEBOARD THAT CONTAINS ALL THE SECTIONS
import React, { useEffect, useState } from 'react';
import {
  LayoutPanelLeftIcon,
  UserCircle,
  Mail,
  Clock,
  ChevronDown,
  ChevronUp,
  ChevronRight,
  Wand2,
  Settings as SettingsIcon,
  Shield
} from "lucide-react";

import { motion, AnimatePresence } from "framer-motion"
import { useAuth } from '../Contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import TokenChecker from "./TokenChecker"


const Button = ({ variant = "default", className = "", children, ...props }) => {
  const baseStyles = "px-4 py-2 rounded-md font-medium gap-x-3 font-semibold"
  const variantStyles = {
    default: "bg-[#4299E1] text-white hover:bg-[#3182CE]",
    outline: "border border-[#4299E1] text-[#4299E1] hover:bg-[#EBF8FF]",
    ghost: "text-[#4A5568] hover:text-[#4299E1] hover:bg-[#EBF8FF]",
    cta: "bg-[#4299E1] text-white shadow-sm hover:bg-[#3182CE]"
  }

  return (
    <button className={`${baseStyles} ${variantStyles[variant]} ${className}`} {...props}>
      {children}
    </button>
  )
}

const UserFooter = () => {
  const { authData, logout } = useAuth();
  const fullName = `${authData.firstName || ""} ${authData.lastName || ""}`.trim();
  const navigate = useNavigate();
  const handleLogout = () => {
    logout();
    navigate('/login'); // Redirect to login page after logout
  };
  return (
    <div className="mt-2 border-t pt-4">
      <div className="text-sm text-[#4A5568] mb-2 text-center">
        <p className="font-semibold">{fullName || "Welcome"}</p>
        <p className="text-xs text-gray-500">{authData.email}</p>
      </div>
      <button
        onClick={handleLogout}
        className="w-full text-sm font-medium text-white bg-[#4299E1] hover:bg-[#3182CE] py-2 px-4 rounded-md transition duration-200 shadow"
      >
        Logout
      </button>
    </div>
  );
};

const Sideboard = ({onCategorySelect, activeCategory, personalActive}) => {
  const [invitedOpen, setInvitedOpen] = useState(false)
  const [assignedOpen, setAssignedOpen] = useState(false)
  const [isStaff, setIsStaff] = useState(false)

  const navigate = useNavigate();

  useEffect(() => {
    TokenChecker('/api/flowsync/admin/stats/', { method: 'GET' })
      .then(res => { if (res.ok) setIsStaff(true) })
      .catch(() => {})
  }, [])

  const toggleInvited = () => {
    setInvitedOpen(!invitedOpen);
  }
  
  const toggleAssigned = () => {
    setAssignedOpen(!assignedOpen);
  }

  // Auto-expand sections when their categories are active
  useEffect(() => {
    if (activeCategory === "Invited Tasks" || activeCategory === "Tasks Delegated - Invitation") {
      setInvitedOpen(true)
    }
    if (activeCategory === "Assigned Tasks" || activeCategory === "Tasks Delegated - Assigned") {
      setAssignedOpen(true)
    }
  }, [activeCategory])

  const SubButton = ({ onClick, active, children }) => (
    <Button
      variant="ghost"
      className={`w-full flex items-center pl-14 py-2 my-3 text-sm ${
        active ? "bg-[#EBF8FF] text-[#4299E1]" : "text-[#4A5568]"
      }`}
      onClick={onClick}
    >
      {children}
    </Button>
  )

  return (
    <div>
      <aside className="w-[17rem] h-screen bg-white p-6 shadow-sm flex flex-col">
        <h1 className="text-4xl pl-6 font-extrabold tracking-tight bg-gradient-to-r from-[#10A7F3] to-[#10F3D6] bg-clip-text text-transparent mb-6">FLOWMAN</h1>
        {/* FLOWSYNC CTA */}

        <div className='mb-6 relative'>
          <div className="pointer-events-none absolute -top-2 -right-2 h-16 w-16 rounded-full bg-[#E6F2FD] blur-2xl"></div>
          <button
            onClick={() => navigate('/flowsync-setup')}
            className='group w-full rounded-xl border border-slate-200 bg-white p-4 text-left shadow-sm hover:border-[#4299E1]/50 hover:shadow transition'
          >
            <div className="flex items-center gap-3">
              <div className="flex h-9 w-9 items-center justify-center rounded-lg  text-[#4299E1] ">
                <Wand2 className="h-5 w-5" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-2xl font-extrabold tracking-tight bg-gradient-to-r from-[#10A7F3] to-[#10F3D6] bg-clip-text text-transparent mb-1">FlowSync</span>
                  <span className="text-[10px] font-semibold rounded-full bg-[#E6F6FF] text-[#4299E1] px-2 py-0.5">
                    New
                  </span>
                </div>
                
              </div>
              <ChevronRight className="h-4 w-4 text-slate-400 group-hover:text-[#4299E1] group-hover:translate-x-0.5 transition" />
            </div>
          </button>
        </div>

        <nav className="space-y-8 flex-1">
          <Button
            variant="ghost"
            className={`w-full flex items-center hover:text-[#4299E1] hover:bg-[#EBF8FF] ${
              personalActive === "Dashboard" ? "bg-[#EBF8FF] text-[#4299E1]" : "text-[#4A5568]"
            } `}
            onClick={() => onCategorySelect("Dashboard","")}
          >
            <LayoutPanelLeftIcon
              className={`mr-2 h-5 w-5 ${
                personalActive === "Dashboard" ? "text-[#4299E1]" : "text-[#4A5568]"
              }`}
            />
            <span className={`${personalActive === "Dashboard" ? "text-[#4299E1]" : ""}`}>Dashboard</span>
          </Button>

          <Button
            variant="ghost"
            className={`w-full flex items-center hover:text-[#4299E1] hover:bg-[#EBF8FF] ${
              personalActive === "Personal Tasks" ? "bg-[#EBF8FF] text-[#4299E1]" : "text-[#4A5568]"
            } `}
            onClick={() => onCategorySelect("Personal Tasks","")}
          >
            <UserCircle
              className={`mr-2 h-5 w-5 ${
                personalActive === "Personal Tasks" ? "text-[#4299E1]" : "text-[#4A5568]"
              }`}
            />
            <span className={`${personalActive === "Personal Tasks" ? "text-[#4299E1]" : ""}`}>
              Personal Tasks
            </span>
          </Button>

          {/* Invited Tasks */}
          <div>
            <Button
              variant="ghost"
              className={`w-full flex items-center justify-between hover:text-[#4299E1] hover:bg-[#EBF8FF] ${
                activeCategory === "Invited Tasks" ? "bg-[#EBF8FF] text-[#4299E1]" : "text-[#4A5568]"
              } `}
              onClick={() => { setInvitedOpen(v => !v); onCategorySelect("Invited Tasks") }}
            >
              <div className="flex gap-3 items-center">
                <Mail
                  className={`mr-2 h-5 w-5 ${
                    activeCategory === "Invited Tasks" ? "text-[#4299E1]" : "text-[#4A5568]"
                  }`}
                />
                <span className={`${activeCategory === "Invited Tasks" ? "text-[#4299E1]" : ""}`}>
                  Invited Tasks
                </span>
              </div>
           {invitedOpen ? (
  <ChevronUp
    className="h-4 w-4 ml-4"
    onClick={(e) => { e.stopPropagation(); toggleInvited(); }}
  />
) : (
  <ChevronDown
    className="h-4 w-4 ml-4"
    onClick={(e) => { e.stopPropagation(); toggleInvited(); }}
  />
)}

            </Button>

            <AnimatePresence>
              {invitedOpen && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <SubButton
                    onClick={() => onCategorySelect("Tasks Delegated - Invitation")}
                    active={activeCategory === "Tasks Delegated - Invitation"}
                  >
                    Delegated
                  </SubButton>
                </motion.div>
              )}
            </AnimatePresence>

            
          </div>

          {/* Assigned Tasks */}
          <div>
            <Button
              variant="ghost"
              className={`w-full flex items-center justify-between hover:text-[#4299E1] hover:bg-[#EBF8FF] ${
                activeCategory === "Assigned Tasks" ? "bg-[#EBF8FF] text-[#4299E1]" : "text-[#4A5568]"
              } `}
              onClick={() => { setAssignedOpen(v => !v); onCategorySelect("Assigned Tasks") }}
            >
              <div className="flex items-center">
                <Clock
                  className={`mr-2 h-5 w-5 ${
                    activeCategory === "Assigned Tasks" ? "text-[#4299E1]" : "text-[#4A5568]"
                  }`}
                />
                <span className={`${activeCategory === "Assigned Tasks" ? "text-[#4299E1]" : ""}`}>
                  Assigned Tasks
                </span>
              </div>
              {assignedOpen ? (
                <ChevronUp className="h-4 w-4" onClick={(e) => { e.stopPropagation(); toggleAssigned(); }} />
              ) : (
                <ChevronDown className="h-4 w-4" onClick={(e) => { e.stopPropagation(); toggleAssigned(); }} />
              )}
            </Button>

            <AnimatePresence>
              {assignedOpen && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <SubButton
                    onClick={() => onCategorySelect("Tasks Delegated - Assigned")}
                    active={activeCategory === "Tasks Delegated - Assigned"}
                  >
                    Delegated
                  </SubButton>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </nav>

        {/* Settings button right above footer's gray line */}
        <div className="mt-8">
          <Button
            variant="ghost"
            className={`w-full flex items-center hover:text-[#4299E1] hover:bg-[#EBF8FF] ${
              personalActive === "Settings" || activeCategory === "Settings"
                ? "bg-[#EBF8FF] text-[#4299E1]"
                : "text-[#4A5568]"
            }`}
            onClick={() => onCategorySelect("Settings","")}
          >
            <SettingsIcon
              className={`mr-2 h-5 w-5  ${
                personalActive === "Settings" || activeCategory === "Settings"
                  ? "text-[#4299E1]"
                  : "text-[#4A5568]"
              }` }
            />
            <span className={`${personalActive === "Settings" || activeCategory === "Settings" ? "text-[#4299E1]" : ""}`}>
              Settings
            </span>
          </Button>

          {isStaff && (
            <Button
              variant="ghost"
              className="w-full flex items-center hover:text-[#4299E1] hover:bg-[#EBF8FF] text-[#4A5568] mt-2"
              onClick={() => navigate('/flowman-admin')}
            >
              <Shield className="mr-2 h-5 w-5 text-[#4A5568]" />
              <span>Admin</span>
            </Button>
          )}
        </div>

        <UserFooter />
      </aside>
    </div>
  );
}

export default Sideboard;
